// ==============================================
// HTTP utility layer (retry + timeout helpers)
// ==============================================
// Thin wrapper around fetch with retries/timeouts. The base URL comes from the
// central config, and the auth token + 401 handling are applied globally by
// services/authFetch.js, so callers never deal with either here.

import { API_URL } from '../config';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Generic request with retries and a per-attempt timeout.
export async function apiRequest(endpoint, options = {}, retries = 5) {
  let lastError;

  for (let i = 0; i < retries; i++) {
    let timeoutId;
    try {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;
      if (i === retries - 1) throw lastError;
      // Exponential backoff: 3s, 6s, 9s, 12s, 15s
      await delay(3000 * (i + 1));
    }
  }
}

// Health check used by the app startup gate.
export async function checkBackendHealth() {
  try {
    const response = await fetch(`${API_URL}/health`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
    });
    return response.ok;
  } catch {
    return false;
  }
}

// Wake a sleeping (free-tier) backend with repeated health pings.
export async function wakeBackend(maxAttempts = 10) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${API_URL}/health`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (response.ok) return true;
    } catch {
      // keep trying
    }
    await delay(5000);
  }
  return false;
}

export async function get(endpoint, retries = 3) {
  return apiRequest(endpoint, { method: 'GET' }, retries);
}

export async function post(endpoint, data, retries = 3) {
  return apiRequest(endpoint, { method: 'POST', body: JSON.stringify(data) }, retries);
}

export async function put(endpoint, data, retries = 3) {
  return apiRequest(endpoint, { method: 'PUT', body: JSON.stringify(data) }, retries);
}

export async function del(endpoint, retries = 3) {
  return apiRequest(endpoint, { method: 'DELETE' }, retries);
}

// FormData upload (no Content-Type so the browser sets the multipart boundary).
export async function postFormData(endpoint, formData, retries = 3) {
  let lastError;
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      lastError = error;
      if (i === retries - 1) throw lastError;
      await delay(3000 * (i + 1));
    }
  }
}

const apiClient = {
  apiRequest,
  checkBackendHealth,
  wakeBackend,
  get,
  post,
  put,
  delete: del,
  postFormData,
};

export default apiClient;
